import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Loader2, Plus, Pencil, Archive, RotateCcw, Search, Check } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Brand Fact", "Program Fact", "Workshop Fact", "Curriculum Topic", "Audience Question",
  "Student Outcome", "Testimonial", "Approved Claim", "Prohibited Claim", "Compliance Rule", "CTA", "FAQ", "Other"];

const empty = () => ({ title: "", category: "Brand Fact", content: "", source_label: "", source_url: "", approved_for_ai: false, active: true });

export default function KnowledgeBase() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const load = () => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (catFilter !== "all") q.set("category", catFilter);
    return api.get(`/marketing/knowledge-base?${q.toString()}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, catFilter]);

  const openCreate = () => { setEditingId(null); setForm(empty()); setOpen(true); };
  const openEdit = (e) => { setEditingId(e.id); setForm({ ...empty(), ...e }); setOpen(true); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/marketing/knowledge-base/${editingId}`, form);
      else await api.post("/marketing/knowledge-base", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const archive = async (e) => { await api.post(`/marketing/knowledge-base/${e.id}/archive`); toast.success("Archived"); load(); };
  const restore = async (e) => { await api.post(`/marketing/knowledge-base/${e.id}/restore`); toast.success("Restored"); load(); };

  return (
    <div data-testid="knowledge-base-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Knowledge Base</h2>
          <p className="text-sm text-gray-500 mt-1">Only <strong>active + approved-for-AI</strong> entries are treated as verified facts by the AI.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="add-kb-button"><Plus className="h-4 w-4 mr-2" /> Add Entry</Button>
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <div className="relative w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search entries" className="pl-9 focus-visible:ring-[#581C87]" data-testid="kb-search" />
        </div>
        <div className="w-52">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="kb-category-filter" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden">
        {items === null ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>
          : items.length === 0 ? <div className="px-6 py-10 text-center text-gray-400 text-sm">No entries.</div>
            : items.map((e) => (
              <div key={e.id} className={`px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4 ${!e.active ? "opacity-60" : ""}`} data-testid={`kb-row-${e.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-gray-900">{e.title}</span>
                    <span className="rounded-full px-2 py-0.5 text-xs bg-gray-100 text-gray-600">{e.category}</span>
                    {e.approved_for_ai && <span className="rounded-full px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><Check className="h-3 w-3" />Approved for AI</span>}
                    {!e.active && <span className="rounded-full px-2 py-0.5 text-xs bg-gray-100 text-gray-500">Archived</span>}
                  </div>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{e.content}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openEdit(e)} data-testid={`edit-kb-${e.id}`} className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]"><Pencil className="h-3.5 w-3.5" /></Button>
                  {e.active
                    ? <Button variant="outline" size="sm" onClick={() => archive(e)} data-testid={`archive-kb-${e.id}`} className="border-gray-200 hover:border-amber-500 hover:text-amber-600"><Archive className="h-3.5 w-3.5" /></Button>
                    : <Button variant="outline" size="sm" onClick={() => restore(e)} data-testid={`restore-kb-${e.id}`} className="border-gray-200 hover:border-emerald-500 hover:text-emerald-600"><RotateCcw className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="kb-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Entry" : "Add Entry"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Title *</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="kb-title-input" className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger data-testid="kb-category-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Content</Label><Textarea value={form.content} onChange={(e) => upd("content", e.target.value)} className="focus-visible:ring-[#581C87] min-h-[100px]" data-testid="kb-content-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Source Label (optional)</Label><Input value={form.source_label} onChange={(e) => upd("source_label", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>Source URL (optional)</Label><Input value={form.source_url} onChange={(e) => upd("source_url", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
              <Label className="cursor-pointer">Approved for AI use</Label>
              <Switch checked={form.approved_for_ai} onCheckedChange={(v) => upd("approved_for_ai", v)} data-testid="kb-approved-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
              <Label className="cursor-pointer">Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => upd("active", v)} data-testid="kb-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="kb-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
